import React, { useState } from 'react';
import { LoadedState, LoadState } from './LoadState';

import './SupportVisualisation.scss';
import { SupportVisualisationViews } from './SupportVisualisationViews';
import SupportVisualisationMenu from './SupportVisualisationMenu';
import Overview from './Content/Overview';
import ViewJson from './Content/ViewJson';
import Streams from './Content/Streams/Streams';
import styleConstants from './variables.module.scss';
import appStyleConstants from 'styles/variables.module.scss';
import Messages from 'widgets/supportVisualisation/Content/Messages/Messages';
import Requests from 'widgets/supportVisualisation/Content/Requests/Requests';
import SupportVisualisationLocation from 'widgets/supportVisualisation/Content/Location/SupportVisualisationLocation';
import SupportVisualisationConnectivity from 'widgets/supportVisualisation/Content/Connectivity/SupportVisualisationConnectivity';
import { useWidgetContext } from 'widgets/WidgetContext';
import StreamTest from 'widgets/supportVisualisation/Content/StreamTest/StreamTest';
import SupportVisualisationEmbeddedApi from 'widgets/supportVisualisation/Content/EmbeddedApi/SupportVisualisationEmbeddedApi';
import SupportVisualisationEmbeddedApiTest from 'widgets/supportVisualisation/Content/EmbeddedApiTest/SupportVisualisationEmbeddedApiTest';
import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import { useGetSupportState } from 'app/contactSupport/store/hooks';
import SupportVisualisationStreamedData from 'widgets/supportVisualisation/Content/StreamedData/SupportVisualisationStreamedData';
import MeasuredMemoryPanel from 'widgets/supportVisualisation/Content/MeasuredMemory/MeasuredMemoryPanel';
import SupportVisualisationScreenshot from 'widgets/supportVisualisation/Content/Screenshot/SupportVisualisationScreenshot';
import SupportVisualisationCurrentFeatureToggles from 'widgets/supportVisualisation/Content/CurrentFeatureToggles/SupportVisualisationCurrentFeatureToggles';
import classNames from 'classnames';
import { isSupportVisPopup } from 'widgets/supportVisualisation/SupportVisualisationPopup/popup';
import SupportVisualisationMarketData from 'widgets/supportVisualisation/Content/MarketData/SupportVisualisationMarketData';
import SupportVisualisationBloombergMessages from 'widgets/supportVisualisation/Content/BloombergMessages/SupportVisualisationBloombergMessages';
import { SupportVisualisationLayout } from 'widgets/supportVisualisation/Content/Layout/SupportVisualiationLayout';
import SupportVisualisationAvailabilityStatus from 'widgets/supportVisualisation/Content/AvailabilityStatus/SupportVisualisationAvailabilityStatus';
import TradeDatesTest from 'widgets/supportVisualisation/Content/TradeDatesTest/TradeDatesTest.tsx';

const padding = 2 * parseInt(appStyleConstants.spaceAround);

export default function SupportVisualisation() {
    const { height, width } = useWidgetContext();

    const [loadedState, setLoadedState] = useState<LoadedState | undefined>(
        undefined
    );

    const [activeView, setActiveView] =
        useState<SupportVisualisationViews>('loadJson');
    const [viewTick, setViewTick] = useState(0);
    const getSupportState = useGetSupportState();
    const onLoadState = (loadedState: LoadedState) => {
        setLoadedState(loadedState);
        setActiveView('overview');
    };

    const borderWidth = 2 * parseInt(appStyleConstants.layoutBorderWidth);
    const widgetWidth = width - borderWidth;

    const contentPadding = parseInt(styleConstants.contentPadding);
    const menuWidth = parseInt(styleConstants.menuWidth);
    const contentHeight = height - padding - borderWidth;
    const contentWidth = widgetWidth - menuWidth - contentPadding * 3;

    const onChangeView = (view: SupportVisualisationViews) => {
        setViewTick(viewTick + 1);
        setActiveView(view);
    };

    const loadCurrentState = () => {
        const state = getSupportState();
        onLoadState({ source: 'Current', state });
    };

    const detailProps: VisualisationContextProps = {
        height: contentHeight,
        width: contentWidth,
        state: loadedState?.state,
        loadCurrentState,
        setActiveView: onChangeView,
    };

    const cssClasses = classNames(
        'SupportVisualisation',
        isSupportVisPopup() ? 'popup' : ''
    );

    return (
        <div className={cssClasses}>
            <div className="loaded-state">
                <SupportVisualisationMenu
                    activeView={activeView}
                    onChangeView={onChangeView}
                    loadedState={loadedState}
                />
                {renderContent()}
            </div>
        </div>
    );

    function renderContent() {
        return (
            <div className="content">
                {activeView === 'overview' && (
                    <Overview
                        {...detailProps}
                        source={loadedState!.source}
                        viewTick={viewTick}
                    />
                )}
                {activeView === 'streams' && <Streams {...detailProps} />}
                {activeView === 'streamedData' && (
                    <SupportVisualisationStreamedData {...detailProps} />
                )}
                {activeView === 'marketData' && (
                    <SupportVisualisationMarketData {...detailProps} />
                )}
                {activeView === 'bloombergMessages' && (
                    <SupportVisualisationBloombergMessages {...detailProps} />
                )}
                {activeView === 'streamtest' && <StreamTest {...detailProps} />}
                {activeView === 'tradedatestest' && (
                    <TradeDatesTest {...detailProps} />
                )}
                {activeView === 'requests' && <Requests {...detailProps} />}

                {activeView === 'messages' && <Messages {...detailProps} />}

                {activeView === 'location' && (
                    <SupportVisualisationLocation {...detailProps} />
                )}
                {activeView === 'layout' && (
                    <SupportVisualisationLayout {...detailProps} />
                )}
                {activeView === 'availabilityStatus' && (
                    <SupportVisualisationAvailabilityStatus {...detailProps} />
                )}
                {activeView === 'connectivity' && (
                    <SupportVisualisationConnectivity {...detailProps} />
                )}

                {activeView === 'viewJson' && <ViewJson {...detailProps} />}
                {activeView === 'embeddedApiMessages' && (
                    <SupportVisualisationEmbeddedApi {...detailProps} />
                )}
                {activeView === 'screenshot' && (
                    <SupportVisualisationScreenshot {...detailProps} />
                )}
                {activeView === 'embeddedApiTest' && (
                    <SupportVisualisationEmbeddedApiTest {...detailProps} />
                )}
                {activeView === 'currentFeatureToggles' && (
                    <SupportVisualisationCurrentFeatureToggles />
                )}
                {activeView === 'loadJson' && renderLoadState()}
                {activeView === 'measuredMemory' && (
                    <MeasuredMemoryPanel {...detailProps} />
                )}
            </div>
        );
    }

    function renderLoadState() {
        return <LoadState onLoadState={onLoadState} />;
    }
}
